<template>
  <el-card>
    <el-form
      v-if="automation && !loading"
      ref="formRef"
      :rules="rules"
      :model="automation"
      :label-position="isDesktop ? 'left' : 'top'"
      :class="{ desktop: isDesktop }"
      label-width="200px"
      @submit.prevent
    >
      <!-- Event Type -->
      <el-form-item label="Notification Type" prop="event_type">
        <el-select
          v-model="automation.label"
          placeholder="Select notification type"
          :disabled="!!automation.id"
          @change="onEventTypeChange"
        >
          <el-option-group
            v-for="group in automationEventGroups"
            :key="group.value"
            :label="group.label"
          >
            <el-option
              v-for="event in group.events"
              :key="event.subtype"
              :label="event.label"
              :value="event"
            />
          </el-option-group>
        </el-select>
      </el-form-item>

      <!-- Name -->
      <el-form-item label="Name" prop="name">
        <el-input v-model="automation.name" @update:modelValue="input" />
      </el-form-item>

      <!-- Filters -->
      <EventFilters
        v-model="automation.filters"
        :event-key="automation.subtype"
      />

      <!-- Active -->
      <el-form-item label="Status">
        <el-radio-group v-model="automation.active">
          <el-radio-button :value="true">Active</el-radio-button>
          <el-radio-button :value="false">Disabled</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <!-- Transport -->
      <el-form-item prop="transport">
        <template #label>
          Transport
          <el-tooltip
            class="item"
            effect="dark"
            content="For each transport, a separate notification should be created."
            placement="top"
          >
            <el-icon class="ml-1">
              <QuestionFilledElementIcon />
            </el-icon>
          </el-tooltip>
        </template>
        <el-radio-group v-model="automation.transport">
          <el-radio-button
            v-for="transport in transports"
            :key="transport"
            :value="transport"
          >
            <font-awesome-icon
              :icon="transportIcon(transport)"
              class="transport-icon"
            />
            {{ transportMeta[transport].label }}
          </el-radio-button>
        </el-radio-group>
      </el-form-item>

      <el-form-item>
        <template v-if="automation.transport === 'telegram' && !telegram">
          <el-button
            type="primary"
            :link="true"
            class="transport-status"
            @click="connectTelegram"
          >
            Connect Telegram
          </el-button>
          <span class="transport-status">or choose another transport</span>
        </template>
        <template v-else>
          <span class="transport-status" v-html="transportInfo" />
        </template>
      </el-form-item>

      <!-- Message -->
      <el-form-item label="Message" prop="message">
        <el-input
          v-model="automation.message"
          disabled
          type="textarea"
          :rows="4"
          @update:modelValue="input"
        />
      </el-form-item>

      <!-- Buttons -->
      <el-form-item>
        <el-button
          :disabled="isBtnDisabled"
          type="primary"
          plain
          @click.prevent="onSubmit(false)"
        >
          {{ actionText }}
        </el-button>
        <router-link to="/notifications" class="el-button is-plain">
          <span>Cancel</span>
        </router-link>
      </el-form-item>
    </el-form>
  </el-card>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'

import useResize from '@/hooks/useResize'
import { transportMeta, transportIcon } from '@/utils/automations'
import { automationEvents } from '@/utils/automation_events'
import { useAutomationsStore } from '@/store/modules/automations'
import EventFilters from './form/EventFilters.vue'
import { IAutomationAttributes } from '@/interfaces/automations'
import { IAutomationEvent } from '@/interfaces/automation_events'
import { useUserStore } from '@/store/modules/user'

export default defineComponent({
  name: 'AutomationForm',
  components: { EventFilters },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: String,
      default: 'Save'
    }
  },
  setup(props, context) {
    const { isDesktop } = useResize()
    const userStore = useUserStore()
    const { telegram, email, GetTelegramLink } = userStore

    const automationsStore = useAutomationsStore()
    const automation = ref<IAutomationAttributes>({ ...props.modelValue })
    const transports = computed(() => automationsStore.transports)

    const automationEventGroups = computed(() => {
      const groups = new Map<string, IAutomationEvent[]>()
      automationEvents.forEach((event) => {
        groups.set(event.value, [...(groups.get(event.value) || []), event])
      })

      return Array.from(groups, ([value, events]) => ({
        value,
        label:
          automationsStore.events.find((event) => event.value === value)
            ?.label || value,
        events
      }))
    })

    const requiredFields = ['name', 'event_type', 'transport', 'message']
    const defaultRule = {
      required: true,
      message: 'This field is required',
      trigger: 'blur'
    }

    const defaultRules = Object.fromEntries(
      requiredFields.map((f) => [f, [defaultRule]])
    )

    const rules = computed(() => {
      const filterOptionRules = {}

      automation.value.filter_options?.forEach((option) => {
        if (option.options?.required) {
          filterOptionRules[`filters.${option.name}.value`] = [defaultRule]
        }
      })

      return { ...defaultRules, ...filterOptionRules }
    })

    const formRef = ref(null)

    const input = async (val: any) => {
      await context.emit('update:modelValue', automation.value)
    }

    const onSubmit = () => {
      // @ts-ignore: Object is possibly 'null'.
      formRef.value.validate(async (valid: boolean) => {
        if (valid) {
          context.emit('submit', {
            automation: automation.value
          })
        }
      })
    }

    const onEventTypeChange = (value: any) => {
      automation.value.event_type = value.value
      automation.value.subtype = value.subtype
      automation.value.filters = value.filters || {}
      automation.value.filter_options = value.filter_options || []
      automation.value.label = value.label
      automation.value.name = value.name
      automation.value.message = value.message
    }

    const transportInfo = computed(() => {
      const map: Record<string, string> = {
        plain_email: `Email: <b>${email}</b>`,
        telegram: `Connected account: <b>@${telegram}</b>`
      }

      return map[automation.value.transport] || ''
    })

    const connectTelegram = async () => {
      const link = await userStore.GetTelegramLink()
      window.open(link, '_blank')
    }

    const isBtnDisabled = computed(() => {
      return automation.value.transport === 'telegram' && !telegram
    })

    return {
      onSubmit,
      isDesktop,
      automation,
      formRef,
      input,
      transportMeta,
      transportIcon,
      rules,
      automationEventGroups,
      onEventTypeChange,
      transports,
      transportInfo,
      isBtnDisabled,
      telegram,
      connectTelegram
    }
  }
})
</script>
<style scoped lang="scss">
:deep(.el-collapse) {
  width: 100%;
}
.transport-icon {
  margin-right: 5px;
}
.transport-status {
  line-height: 14px;
  padding: 0;
  margin: 0;
  height: 16px;
  align-content: center;
}
span.transport-status {
  color: var(--el-text-color-regular);
  margin-left: 0.5em;
}
</style>
